import logging from "utils/logging";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { styled } from '@mui/material/styles';
import {
  selectGameId,
  selectGameObject,
  selectGameStatusObject,
} from "../gamestate/gameStateSlice";
import Question from "features/question/Question";
import MakePlayer from "features/player/MakePlayer";
import {
  getPlayerForUserInGameAction,
  selectUserId,
  selectPlayer,
  selectLoadingPlayer,
} from "features/player/playerSlice";
import {
  CLIENT_QUESTIONVIEW_ROUNDSTATES,
  GAMESTATE_CREDITS,
  GAMESTATE_LOBBY,
  GAMESTATE_ROUND,
  GAMESTATE_ROUND_FINAL,
  GAMESTATE_ROUNDSTATE_ROUND_INTRO,
  GAMESTATE_ROUNDSTATE_QUESTION_WAGERING,
  GAMESTATE_ROUNDSTATE_WAITING,
  GAMESTATE_ROUNDSTATE_FINALROUND_CATEGORY_PREVIEW,
  GAMESTATE_ROUNDSTATE_FINALROUND_WAGER_INSTRUCTIONS,
  GAMESTATE_INSTRUCTIONS,
} from "../gamestate/constants";
import PlayerLeaderboard from "../leaderboard/PlayerLeaderboard";
import { Wagering } from "./Wagering";
import {
  stopWatchingSubmissionAction,
  watchSingleSubmissionAction,
} from "../question/submissionSlice";
import PlayerInstructionsScreen from "./PlayerInstructionsScreen";
import ClueSelector from "./ClueSelector/ClueSelector";
import WaitingForClue from "./WaitingForClue";
import FinalCategoryPreview from "./FinalCategoryPreview";
import PlayerInterRoundScreen from "./PlayerInterRoundScreen";
import PhoneReveal from "../superlatives/components/PhoneReveal";
import PhoneShareCards from "../superlatives/components/PhoneShareCards";
import { selectSuperlativesStatus } from "../superlatives/superlativeSlice";
import { SUPERLATIVES_STATUS_FINALE } from "../../provider/firebase/constants";

const log = logging.getLogger("GameComponent"); //eslint-disable-line no-unused-vars

const Root = styled('div')(({ theme }) => ({
  textAlign: "center",
  height: "100%",
}));

const LoadingStack = styled(Stack)(({ theme }) => ({
  width: "100%",
  height: "100%",
  minHeight: "300px",
}));

const StyledDivider = styled(Divider)(({ theme }) => ({
  // backgroundColor: "#dddddd",
}));

const Header = styled(Typography)(({ theme }) => ({
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(1),
  fontSize: "2em",
  fontFamily: "Gyparody",
}));

const Header2 = styled(Typography)(({ theme }) => ({
  paddingBottom: theme.spacing(1),
  fontSize: "2em",
  fontFamily: "Helvetica Inserat",
}));

const BodyDiv = styled('div')(({ theme }) => ({
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(2),
}));

const WaitingText = styled(Typography)(({ theme }) => ({
  fontFamily: "Korinna",
}));

const Game = () => {
  const dispatch = useDispatch();

  const gameStatus = useSelector(selectGameStatusObject);
  const game = useSelector(selectGameObject);
  const gameId = useSelector(selectGameId);
  const loadingPlayer = useSelector(selectLoadingPlayer);
  const userId = useSelector(selectUserId);
  const player = useSelector(selectPlayer);
  const superlativesStatus = useSelector(selectSuperlativesStatus);

  const playerId = player ? player.id : null;
  const { questionId } = gameStatus;

  // ---- Effects
  useEffect(() => {
    if (gameId && playerId && questionId) {
      dispatch(watchSingleSubmissionAction(gameId, playerId, questionId));
    }

    return function cleanup() {
      dispatch(stopWatchingSubmissionAction(gameId, questionId, playerId));
    };
  }, [gameId, playerId, questionId, dispatch]);

  useEffect(() => {
    // If the game is loaded, load the player.
    log.debug("Game.js - useEffect – gameId", gameId, "userId", userId);
    if (gameId && userId) {
      dispatch(getPlayerForUserInGameAction(userId, gameId));
    }
  }, [gameId, userId, dispatch]);

  log.debug("Game.js gameStatus", gameStatus, "loading player: ", loadingPlayer);

  // If we don't have a game yet, show a loading screen
  let body = <div />;
  if (loadingPlayer) {
    body = (
      <LoadingStack
        direction="row"
        justifyContent="center"
        alignItems="center"
      >
        <CircularProgress />
      </LoadingStack>
    );
  } else if (game && !player) {
    // If we have a game, but don't have a player, show the player join screen
    body = <MakePlayer gameId={game.id} userId={userId} />;
  } else if (gameStatus && gameStatus.currentState === GAMESTATE_INSTRUCTIONS) {
    // If we're in the instructions state, show the PlayerInstructionsScreen
    body = <PlayerInstructionsScreen />;
  } else if (gameStatus && gameStatus.currentState === GAMESTATE_LOBBY && gameStatus.round) {
    // Inter-round lobby - player ready-up screen
    body = <PlayerInterRoundScreen />;
  } else if (gameStatus && gameStatus.currentState === GAMESTATE_CREDITS) {
    body = superlativesStatus === SUPERLATIVES_STATUS_FINALE
      ? <PhoneShareCards />
      : <PhoneReveal />;
  } else {
    // We have the player & the game, make the gameplay screens based on state
    let gameBody = <div />;
    if (
      gameStatus &&
      gameStatus.currentState === GAMESTATE_ROUND &&
      gameStatus.roundState === GAMESTATE_ROUNDSTATE_ROUND_INTRO
    ) {
      const roundNames = {
        practice: 'Practice Round',
        round1: 'Round 1',
        round2: 'Double Groupardy',
        round3: 'Triple Groupardy',
      };
      const round = gameStatus.round;
      const roundName = roundNames[round] || round;
      const categories = game?.categories?.[round] || [];
      const revealedCount = gameStatus?.roundIntro?.revealedCount || 0;
      gameBody = (
        <div style={{ textAlign: 'center', padding: '16px' }}>
          <Typography variant="h4" sx={{ fontFamily: 'Gyparody', mb: 3 }}>
            {roundName}
          </Typography>
          <Stack spacing={1} alignItems="center">
            {categories.slice(0, revealedCount).map((cat, idx) => (
              <Typography
                key={idx}
                variant="h6"
                sx={{
                  fontFamily: 'Helvetica Inserat',
                  color: '#2ec4b6',
                  textTransform: 'uppercase',
                  letterSpacing: '2px',
                }}
              >
                {cat?.category}
              </Typography>
            ))}
          </Stack>
        </div>
      );
    } else if (
      gameStatus &&
      gameStatus.currentState === GAMESTATE_ROUND &&
      gameStatus.dailyDouble
    ) {
      let myDD = gameStatus.playerInControl === player.id;
      log.debug("It's my DD: ", myDD);
      if (myDD) {
        if (gameStatus.roundState === GAMESTATE_ROUNDSTATE_QUESTION_WAGERING) {
          gameBody = <Wagering />;
        } else if (
          CLIENT_QUESTIONVIEW_ROUNDSTATES.includes(gameStatus.roundState)
        ) {
          gameBody = (
            <Question
              questionId={questionId}
              gameId={game.id}
              userId={userId}
              playerId={player.id}
            />
          );
        }
      } else {
        gameBody = (
          <WaitingText variant="h4">
            Daily Double!
          </WaitingText>
        );
      }
    } else if (
      gameStatus &&
      gameStatus.currentState === GAMESTATE_ROUND &&
      CLIENT_QUESTIONVIEW_ROUNDSTATES.includes(gameStatus.roundState)
    ) {
      gameBody = (
        <Question
          questionId={questionId}
          gameId={game.id}
          userId={userId}
          playerId={player.id}
        />
      );
    } else if (
      gameStatus &&
      gameStatus.currentState === "round" &&
      gameStatus.round !== GAMESTATE_ROUND_FINAL &&
      gameStatus.roundState === GAMESTATE_ROUNDSTATE_WAITING
    ) {
      let mePicking = gameStatus.playerInControl === player.id;
      gameBody = mePicking ? (
        <ClueSelector />
      ) : (
        <WaitingForClue />
      );
    } else if (
      gameStatus &&
      gameStatus.currentState === "round" &&
      gameStatus.round === GAMESTATE_ROUND_FINAL &&
      (gameStatus.roundState === GAMESTATE_ROUNDSTATE_FINALROUND_CATEGORY_PREVIEW ||
        gameStatus.roundState === GAMESTATE_ROUNDSTATE_FINALROUND_WAGER_INSTRUCTIONS)
    ) {
      gameBody = <FinalCategoryPreview />;
    } else if (
      gameStatus &&
      gameStatus.currentState === "round" &&
      gameStatus.round === GAMESTATE_ROUND_FINAL &&
      gameStatus.roundState === GAMESTATE_ROUNDSTATE_QUESTION_WAGERING
    ) {
      gameBody = <Wagering />;
    }

    body = (
      <div>
        {game && (
          <div>
            <Header variant="h4">
              {game.title}
            </Header>
            <Header2 variant="h5">
              {game.subtitle}
            </Header2>
            <StyledDivider />
            <BodyDiv>
              {gameBody}
            </BodyDiv>
          </div>
        )}
      </div>
    );
  }

  return (
    <Root>
      {body}
      {player && gameStatus?.currentState !== GAMESTATE_INSTRUCTIONS && gameStatus?.currentState !== GAMESTATE_CREDITS && !(gameStatus?.currentState === GAMESTATE_LOBBY && gameStatus?.round) && <PlayerLeaderboard />}
    </Root>
  );
};

export default Game;
